import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentConsentDecide() {
  const { token } = useParams();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/parent-consent/${token}`)
      .then(res => setRec(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired link'))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (decision) => {
    if (!acknowledged) { toast.error('Please confirm you have read the disclosures'); return; }
    if (!confirmEmail) { toast.error('Enter your email to confirm identity'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/parent-consent/${token}/decide`, {
        decision,
        parent_email: confirmEmail
      });
      toast.success(decision === 'approve' ? 'Approved — thank you' : 'Denied');
      setRec({ ...rec, status: decision === 'approve' ? 'approved' : 'denied' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="mono-data text-sm animate-pulse-red">Loading…</div></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-sm">
        <XCircle className="w-10 h-10 text-[#09090B] mx-auto mb-3" />
        <p className="text-sm text-zinc-600">{error}</p>
      </div>
    </div>
  );

  if (rec.status === 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="consent-approved">
        <div className="text-center max-w-sm">
          <CheckCircle className="w-12 h-12 text-[#09090B] mx-auto mb-3" />
          <h1 className="text-2xl font-black tracking-tighter mb-2">Consent Approved</h1>
          <p className="text-sm text-zinc-600 mb-6">{rec.child_name || rec.child_email} can now use FOKYES.</p>
          <Link to="/" className="text-[#09090B] underline text-sm">Return home</Link>
        </div>
      </div>
    );
  }

  if (rec.status === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="consent-denied">
        <div className="text-center max-w-sm">
          <XCircle className="w-12 h-12 text-[#09090B] mx-auto mb-3" />
          <h1 className="text-2xl font-black tracking-tighter mb-2">Consent Denied</h1>
          <p className="text-sm text-zinc-600">The child's account has been deactivated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="parent-consent-page">
      <div className="container-swiss max-w-2xl">
        <div className="border border-[#09090B] p-8">
          <ShieldCheck className="w-10 h-10 text-[#09090B] mb-4" />
          <p className="mono-data text-[#09090B] mb-1">Parental Consent Required</p>
          <h1 className="text-3xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Approve access for your child?
          </h1>

          <div className="border-l-4 border-[#09090B] bg-zinc-100 px-4 py-3 mb-6">
            <p className="text-sm">
              <strong>{rec.child_name || rec.child_email}</strong> (age <strong>{rec.child_age}</strong>)
              wants to use FOKYES for cognitive training. As their parent/guardian, your consent is required
              under India's DPDP Act, US COPPA, and EU GDPR-K.
            </p>
          </div>

          <h3 className="mono-data text-xs text-[#09090B] mb-3">What you're consenting to</h3>
          <ul className="text-sm text-zinc-700 space-y-2 mb-6 list-disc pl-5">
            <li>Collection of your child's date of birth, session performance data, and BrainMap scores</li>
            <li>Use of AI (OpenAI) to adapt session difficulty based on anonymised performance</li>
            <li>Your ability to monitor progress via the Parent Dashboard (linked to your email)</li>
            <li>No behavioural or targeted advertising shown to your child</li>
            <li>Right to revoke consent and delete all data at any time</li>
          </ul>

          <div className="border border-[#09090B] bg-zinc-100 p-3 mb-6 flex gap-2 items-start">
            <AlertTriangle className="w-4 h-4 text-[#09090B] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-zinc-700">
              <strong>Medical disclaimer:</strong> The BrainMap Score is an educational metric, not a clinical
              or diagnostic assessment. It does not diagnose or treat any condition.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mono-data text-xs text-zinc-500 mb-1 block">Confirm your email for identity verification</label>
              <input type="email" value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)}
                placeholder={rec.parent_email} className="input-swiss text-sm w-full" data-testid="parent-confirm-email" />
            </div>

            <label className="flex items-start gap-3 text-xs cursor-pointer">
              <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}
                className="mt-0.5" data-testid="parent-acknowledge" />
              <span>I am the legal parent/guardian of this child and I have read the{' '}
                <Link to="/privacy" target="_blank" className="text-[#09090B] underline">Privacy Policy</Link> and{' '}
                <Link to="/terms" target="_blank" className="text-[#09090B] underline">Terms of Service</Link>.</span>
            </label>

            <div className="flex gap-3 pt-2">
              <button onClick={() => decide('approve')} disabled={submitting}
                className="flex-1 btn-primary py-3 text-sm disabled:opacity-40" data-testid="approve-btn">
                Approve & Allow Access
              </button>
              <button onClick={() => decide('deny')} disabled={submitting}
                className="flex-1 btn-secondary py-3 text-sm disabled:opacity-40" data-testid="deny-btn">
                Deny
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
